import { useUpdateJobInboundSettingsMutation } from '@/modules/jobs/mutations/useUpdateJobInboundSettingsMutation';
import { useJobInboundSettingsQuery } from '@/modules/jobs/queries/useJobInboundSettingsQuery';
import { InlineError } from '@/modules/ui/components/inline-error/InlineError';
import Loader from '@/modules/ui/components/loader/Loader';
import { useSavedFlash } from '@/modules/ui/components/saved-indicator/useSavedFlash';
import { actionErrorMessage } from '@/utils/actionErrorMessage';

import {
  JobInboundSettings,
  type JobInboundSettingsValues,
} from './JobInboundSettings';

interface JobInboundSettingsSectionProps {
  jobId: string;
}

export function JobInboundSettingsSection({
  jobId,
}: JobInboundSettingsSectionProps) {
  const settingsQuery = useJobInboundSettingsQuery(jobId);
  const updateMutation = useUpdateJobInboundSettingsMutation();
  const { isSaved, markSaved, clearSaved } = useSavedFlash();

  if (settingsQuery.isLoading) return <Loader />;

  if (!settingsQuery.data) {
    return (
      <InlineError>
        {actionErrorMessage('load the inbound settings', settingsQuery.error)}
      </InlineError>
    );
  }

  const handleSave = (values: JobInboundSettingsValues) => {
    clearSaved();
    updateMutation.mutate({ jobId, ...values }, { onSuccess: markSaved });
  };

  const handleRetry = () => {
    if (updateMutation.variables) {
      const { isInboundEnabled, publicJobDescription } =
        updateMutation.variables;
      handleSave({ isInboundEnabled, publicJobDescription });
    }
  };

  return (
    <JobInboundSettings
      settings={settingsQuery.data}
      isPending={updateMutation.isPending}
      isSaved={isSaved}
      error={
        updateMutation.isError
          ? actionErrorMessage(
              'save the inbound settings',
              updateMutation.error,
            )
          : null
      }
      onSave={handleSave}
      onRetry={handleRetry}
    />
  );
}
